import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { analyticListLimit, analyticListLimits } from "configs/limits";

import Pagination from "components/Pagination";
import FilterTabs from "components/FilterTabs";
import Tooltip from "components/Tooltip";
import EmptyState from "components/EmptyState/EmptyState";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import ImageWithFallback from "helpers/ImageWithFallback";

interface ListItem {
  date: string;
  items: {
    usersCount: number;
    betCountReal: number;
    betSumReal: number;
    betCountBonus: number;
    betSumBonus: number;
    winCountReal: number;
    winSumReal: number;
    winCountBonus: number;
    winSumBonus: number;
    turnoverReal: number;
    turnoverBonus: number;
    turnoverTotal: number;
    ggrReal: number;
    ggrBonus: number;
    ggrTotal: number;
    gameTitle: string;
    gameImage: string;
  }[];
}
const AnalyticGamesList: React.FC = () => {
  const { t } = useTranslation();

  const [isFetched, setIsFetched] = useState(false);
  const [dateFrom, setDateFrom] = useState(
    moment().subtract(1, "week").toDate(),
  );
  const [dateTo, setDateTo] = useState(new Date());
  const [type, setType] = useState("day");
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(analyticListLimit);
  const [items, setItems] = useState<ListItem[]>([]);

  const handleLoadItems = async () => {
    setIsFetched(false);
    try {
      const result = await instance.post(`${apiRoutes.gamesAnalytics}`, {
        startDate: moment(dateFrom).format("DD.MM.YYYY"),
        endDate: moment(dateTo).format("DD.MM.YYYY"),
        period: type,
        offset: activePage - 1,
        limit: currentLimit,
      });
      setItems(result.data.items);
      setTotalItems(result.data.totalItems);
      setTotalPages(result.data.totalPages);
    } catch {
      console.log("handleLoadItems error");
    }
    setIsFetched(true);
  };

  useEffect(() => {
    handleLoadItems();
  }, [dateFrom, dateTo, activePage, type, currentLimit]);

  return (
    <div className="page-list-wrapper analytic-wrapper">
      <div className="page-list-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            <div className="dashboard-filter">
              <div className="row">
                <div className="dashboard-date">
                  <span className="label">{t("from")}:</span>
                  <div className="date-picker-wrapper">
                    <button
                      onClick={() =>
                        setDateFrom(
                          moment(dateFrom).subtract(1, "days").toDate(),
                        )
                      }
                    >
                      &lt;
                    </button>
                    <DatePicker
                      customInput={<ReadOnlyInput />}
                      selected={dateFrom}
                      onChange={(date: any) => setDateFrom(date)}
                      dateFormat={"d/MM/YYYY"}
                    />
                    <button
                      onClick={() =>
                        setDateFrom(moment(dateFrom).add(1, "days").toDate())
                      }
                    >
                      &gt;
                    </button>
                  </div>
                </div>
                <div className="dashboard-date">
                  <span className="label">{t("to")}:</span>
                  <div className="date-picker-wrapper">
                    <button
                      onClick={() =>
                        setDateTo(moment(dateTo).subtract(1, "days").toDate())
                      }
                    >
                      &lt;
                    </button>
                    <DatePicker
                      customInput={<ReadOnlyInput />}
                      selected={dateTo}
                      onChange={(date: any) => setDateTo(date)}
                      dateFormat={"d/MM/YYYY"}
                    />
                    <button
                      onClick={() =>
                        setDateTo(moment(dateTo).add(1, "days").toDate())
                      }
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <FilterTabs
              value={type}
              onSelect={(e) => setType(e as string)}
              options={[
                {
                  value: "hour",
                  label: t("hour"),
                },
                {
                  value: "day",
                  label: t("day"),
                },
                {
                  value: "week",
                  label: t("week"),
                },
                {
                  value: "month",
                  label: t("month"),
                },
                {
                  value: "year",
                  label: t("year"),
                },
              ]}
            />
          </div>
        </div>

        {!isFetched && (
          <>
            <DataTableSkeleton columnsCount={18} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isFetched && items.length === 0 && (
          <div className="game-table-wrap table">
            <EmptyState message={t("noGameAnalyticsDataFound")} />
          </div>
        )}

        {isFetched && items.length > 0 && (
          <>
            <div className="game-table-wrap table">
              <table className="game-table">
                <thead>
                  <tr>
                    <th className="separator main">
                      <div className="th-content">
                        {t("date")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("game")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("image")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("users")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("betReal")}
                        <span className="thead-label">cnt</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("betReal")}
                        <span className="thead-label">sum</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("betBonus")}
                        <span className="thead-label">cnt</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("betBonus")}
                        <span className="thead-label">sum</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("winReal")}
                        <span className="thead-label">cnt</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("winReal")}
                        <span className="thead-label">sum</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("winBonus")}
                        <span className="thead-label">cnt</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("winBonus")}
                        <span className="thead-label">sum</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("realTo")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("bonusTo")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("totalTo")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("realGgr")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("bonusGgr")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("totalggr")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((el) =>
                    el.items.map((item, idx) => (
                      <tr key={`${el.date}-${idx}`}>
                        <td className="separator main">
                          {idx === 0
                            ? moment
                                .utc(el.date)
                                .format(
                                  type === "hour"
                                    ? "DD.MM.YYYY HH:mm"
                                    : "DD.MM.YYYY",
                                )
                            : ""}
                        </td>
                        <td>{item.gameTitle}</td>
                        <td>
                          {item.gameTitle && (
                            <ImageWithFallback
                              className="analytic-game-image"
                              src={item.gameImage}
                              alt=""
                            />
                          )}
                        </td>
                        <td>{item.usersCount}</td>
                        <td>{item.betCountReal}</td>
                        <td>{item.betSumReal}</td>
                        <td>{item.betCountBonus}</td>
                        <td>{item.betSumBonus}</td>
                        <td>{item.winCountReal}</td>
                        <td>{item.winSumReal}</td>
                        <td>{item.winCountBonus}</td>
                        <td>{item.winSumBonus}</td>
                        <td>{item.turnoverReal}</td>
                        <td>{item.turnoverBonus}</td>
                        <td>{item.turnoverTotal}</td>
                        <td>{item.ggrReal}</td>
                        <td>{item.ggrBonus}</td>
                        <td>{item.ggrTotal}</td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              activePage={activePage}
              limit={currentLimit}
              totalItems={totalItems}
              totalPages={totalPages}
              limitsArray={analyticListLimits}
              handleLimitClick={(limit) => {
                setCurrentLimit(limit);
                setActivePage(1);
              }}
              handleNextClick={() => {
                if (activePage !== totalPages) setActivePage(activePage + 1);
              }}
              handlePrevClick={() => {
                if (activePage !== 1) setActivePage(activePage - 1);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticGamesList;
